/**
 * The applicant flow link, `/u/<code>`: the applicant token's 16 bytes as
 * unpadded base64url — 22 characters instead of a 36-character uuid in a query
 * string. It is the SAME credential re-encoded, not a second one: the backend
 * builds it (`collection_flow::flow_link`) and this file turns it back into the
 * uuid the API authenticates. `/verify?token=<uuid>` links keep working.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CODE = /^[A-Za-z0-9_-]{22}$/;

/** `ceb76355-da07-…` → `zrdjVdoHRw6QHUDcjtILhQ`; `null` for anything not a uuid. */
export function encodeFlowCode(token: string): string | null {
  if (!UUID.test(token)) return null;
  const hex = token.replace(/-/g, '');
  let binary = '';
  for (let i = 0; i < 32; i += 2) {
    binary += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The uuid inside a flow-link code, or `null` when the code is not one. */
export function decodeFlowCode(code: string): string | null {
  if (!CODE.test(code)) return null;
  const binary = atob(code.replace(/-/g, '+').replace(/_/g, '/') + '==');
  const hex = Array.from(binary, (c) => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
  const token = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  // 22 characters carry 132 bits for 128: sixteen codes spell each token, and
  // only the one the backend emits (trailing bits zero) is accepted, so a link
  // has exactly one spelling.
  return encodeFlowCode(token) === code ? token : null;
}
